import {injectable} from 'inversify';
import {UpdateManager} from '@fantasmagames/core/src/update/UpdateManager';
import {ReelSymbol} from '@fantasmagames/slot/src/modules/reels/spin/ReelSymbol';
import {
	AvalancheConstants,
	ObjectSize, Position,
	ReelConstants,
	SymbolPosition,
} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {Easing, IEasingFunction} from '@fantasmagames/core/src/tween/Easing';
import {Timer} from '@fantasmagames/core/src/update/Timer';
import {AvalancheNewSymbol} from '@fantasmagames/slot/src/logic/SpinResult';
import {SpineUtils} from '@fantasmagames/core/src/spine/SpineUtils';
import {SoundPlayer} from '@fantasmagames/slot/src/sound/SoundPlayer';
import {ReelsUtils} from '@fantasmagames/slot/src/modules/reels/ReelsUtils';
import {ViewTypes} from '@fantasmagames/slot/src/common/view/View';
import {TimerUtils} from '@fantasmagames/core/src/update/TimerUtils';

@injectable()
export class AvalancheReel extends UpdateManager {
	protected static readonly STOP_ANIMATION_TIMER_OFFSET: number = 50;
	protected static readonly DROP_REELS_AVALANCHE_EASING: IEasingFunction = Easing.Exponential.Out;

	// reel symbols
	protected _symbols: ReelSymbol[];

	// reel offsets
	protected _offsets: Position;

	// reel state flags
	protected _isSpinning: boolean = false;

	// reel stopped callback
	protected _onReelDropOutFinished: Function;
	protected _onReelDropNewFinished: Function;
	protected _onReelDropFirstSymbolFinished: Function;
	protected _onFillingVoidsFinished: Function;
	protected _onAddingSymbolsFinished: Function;

	// index of the reel (from 0)
	protected _reelIndex: number;

	// container of the reel (reel itself). used for movement and tweening
	protected _container: PIXI.Sprite;

	protected _initialStrip: number[] = [3, 3, 3, 3, 3, 3, 3, 3, 3, 3];

	// number of rows we're currently showing on this reel
	protected _rowsToShow: number = 5;

	// an index of a highest symbol on the reel
	protected _topVisibleSymbolIndex: number;

	// number of symbols on this reel
	protected _symbolsOnReel: number;

	// is there any symbols hanging in the air to drop down before new symbols will come up
	protected _hasAnythingToDropFillingTheVoid: boolean = false;

	protected _resources: ResourcesLoader;
	protected _soundPlayer: SoundPlayer;

	protected _spineUtils: SpineUtils;
	protected _timerUtils: TimerUtils;

	protected _viewType: ViewTypes;

	constructor(reelIndex: number,
				startReelPosition: number,
				offsets: Position,
				container: PIXI.Sprite,
				viewType: ViewTypes = ViewTypes.DESKTOP) {
		super();

		this._symbols = [];
		this._reelIndex = reelIndex;
		this._offsets = offsets;
		this._container = container;
		this._viewType = viewType;
	}

	public putSymbolOnTopOfOther(symPos: SymbolPosition): void {
		const parent: PIXI.Container = this._symbols[symPos.y].parent;
		parent.setChildIndex(this._symbols[symPos.y], parent.children.length - 1);

	}

	public setupReel(resources: ResourcesLoader, soundPlayer: SoundPlayer): void {
		this._resources = resources;
		this._soundPlayer = soundPlayer;
		this._spineUtils = new SpineUtils(resources, this, this._container);
	}

	public init(initialReel: GameSymbol[] = null): void {
		this.createReel(initialReel);
	}

	public setRowsToShow(rowsToShow: number): void {
		this._rowsToShow = rowsToShow;
	}

	public makeCleanup(): void {
		for (const sym of this._symbols) {
			sym.makeCleanup();
		}
	}

	public initTopVisibleSymbolIndex(initialReel: GameSymbol[]): void {
		this.topVisibleSymbolIndex = this._rowsToShow - initialReel.length;
	}

	public getRowsToShow(): number {
		return this._rowsToShow;
	}

	public recountSymbolsOnReel(): void {
		this.symbolsOnReel = 0;
		for (const sym of this._symbols) {
			if (sym.visible) {
				this.symbolsOnReel++;
			}
		}
	}

	protected updateVisibleRows(): void {
		for (let i = 0; i < ReelConstants.MAX_ROW_AMOUNT; i++) {
			if (i < ReelConstants.MAX_ROW_AMOUNT - this._rowsToShow) {
				this._symbols[i].visible = false;
			} else {
				this._symbols[i].visible = true;
			}
		}
	}

	protected setSymbolInPosition(symbols: GameSymbol[], positionIndex: number, symIndex: number, offset: number): void {
		this._symbols[positionIndex].setSymbol(this.makeSizedSymbol(symbols[symIndex].sym, symbols[symIndex].size));
	}

	public dropSymbols(symbols: GameSymbol[]): void {
		let index: number = 0;
		let offset: number = 0;
		let lastDelay: number = 0;
		let ifFirst: boolean = true;
		this.topVisibleSymbolIndex = this._rowsToShow - symbols.length;
		this.symbolsOnReel = symbols.length;
		for (let i = ReelConstants.MAX_ROW_AMOUNT - 1; i >= ReelConstants.MAX_ROW_AMOUNT - this._rowsToShow; i--) {
			if (index < symbols.length) {
				const symIndex: number = symbols.length - 1 - index;
				offset += symbols[symIndex].size.height - 1;
				this.setSymbolInPosition(symbols, i, symIndex, offset);
				this._symbols[i].visible = true;

				const yToPos: number = this._offsets.y + this.getYPositionForRow(i - offset);
				lastDelay =
					index * AvalancheConstants.TIMER_BETWEEN_SYMBOLS_NEW + this._reelIndex * AvalancheConstants.TIMER_BETWEEN_REELS_NEW;
				const dropNewTween: Tween = this.dropNewSymbolToPosition(i, yToPos, lastDelay);

				if (ifFirst) {
					dropNewTween.onComplete(this.onFirstSymbolDropped.bind(this));
					ifFirst = false;
				}
				if (index === symbols.length - 1) {
					dropNewTween.onComplete(this.onLastSymbolDropped.bind(this));
				}

				index++;
			} else {
				this._symbols[i].visible = false;
			}
		}
	}

	public dropOldSymbolsOut(): void {
		let index: number = 0;
		for (let i = ReelConstants.MAX_ROW_AMOUNT - 1; i >= ReelConstants.MAX_ROW_AMOUNT - this._rowsToShow; i--) {
			const delay: number = index * AvalancheConstants.TIMER_BETWEEN_SYMBOLS_OUT + this._reelIndex * AvalancheConstants.TIMER_BETWEEN_REELS_OUT;
			const dropOutTween: Tween = new Tween(this._symbols[i])
				.to({y: this._symbols[i].y + 1024}, AvalancheConstants.AVALANCHE_OUT_TIMER)
				.delay(delay);
			this.add(dropOutTween);
			dropOutTween.start();
			index++;

			if (i === ReelConstants.MAX_ROW_AMOUNT - this._rowsToShow) {
				dropOutTween.onComplete(this.onLastSymbolDroppedOut.bind(this));
			}
		}
	}

	public removeAvalancheSymbols(positions: SymbolPosition[], onFinishedCallback: Function): void {
		positions = positions.sort((s1, s2) => s1.y - s2.y);
		let isFirst: boolean = true;
		for (const pos of positions) {
			const sym = this._symbols[this.topVisibleSymbolIndex + pos.y];
			this.playRemoveAnimationAt(sym, isFirst ? onFinishedCallback : null);
			isFirst = false;

			this._symbols.splice(this.topVisibleSymbolIndex + pos.y, 1);
			this._symbols.unshift(sym);

			this.symbolsOnReel--;

			for (let rowIndex = 0; rowIndex < ReelConstants.MAX_ROW_AMOUNT; rowIndex++) {
				this._symbols[rowIndex].index = rowIndex;
			}
		}

		this.symbolsOnReel = this.symbolsOnReel < 0 ? 0 : this.symbolsOnReel;
		this.topVisibleSymbolIndex += positions.length;
	}

	/**
	 * handles dropping of the symbols in the air to the floor
	 */
	public dropElementsToFillVoids(): void {
		let offset: number = 0;
		let isFirstTween: boolean = true;
		this._hasAnythingToDropFillingTheVoid = false;
		for (let i = ReelConstants.MAX_ROW_AMOUNT - 1; i >= 0; i--) {
			// 1a. if the symbols is visible (not destroyed)
			if (this._symbols[i].visible) {
				const pos = this._symbols[i].positionOnReel;
				const height = this._symbols[i].size.height;
				// 2a. and it is not on the expected place
				if (pos !== offset + height - 1) {
					const moveOffset = pos - (offset + height - 1);
					const reelPosition = this.getPositionOnReel(pos, moveOffset);
					const newYPos: number = this._offsets.y + this.getYPositionForRow(reelPosition);
					// 3a. then we're dropping it down with a tween
					const tween: Tween = this.dropExistingSymbol(i, newYPos, 0, false);

					this._symbols[i].positionOnReel -= moveOffset;
					this._symbols[i].setContainerParameters();

					if (isFirstTween) {
						isFirstTween = false;
						this._hasAnythingToDropFillingTheVoid = true;
						tween.onComplete(() => {
							this.cleanAfterFall();
							this._onFillingVoidsFinished(this._reelIndex);
						});
					}
				}
				offset += height;
				this.topVisibleSymbolIndex = i;
			} else {
				// 1b. if this is a last element on reel and it is invisible it means
				// there is no symbols left on this reel
				// so top visible element has the index of top element + 1
				// so that next falling symbol we receive 1st position on the reel
				if (i === ReelConstants.MAX_ROW_AMOUNT - 1) {
					this.topVisibleSymbolIndex = ReelConstants.MAX_ROW_AMOUNT;
				}

				return;
			}
		}
	}

	/**
	 * adds new symbols after win lines were destroyed or re-spin took place
	 * @param symbols - array of new symbols
	 * @param onAddingFinishedCallback - callback for when symbols were added
	 */
	public addNewSymbols(symbols: AvalancheNewSymbol[], onAddingFinishedCallback: Function): void {
		symbols = symbols.sort((s1, s2) => s2.row - s1.row);
		this._onAddingSymbolsFinished = onAddingFinishedCallback;
		this.symbolsOnReel += symbols.length;

		let lastTween: Tween;
		let lastDelay: number = 0;

		let isFirst: boolean = true;
		let firstSymbolPos: SymbolPosition;
		let firstSymbolSize: number;

		if (symbols.length === 0) {
			onAddingFinishedCallback && onAddingFinishedCallback();
		}

		// going through all the symbols
		for (let i = 0; i < symbols.length; i++) {
			const symbol = symbols[i];
			// taking highest visible symbol on reel
			const prevSym: ReelSymbol = this._symbols[this.topVisibleSymbolIndex];
			let prevElementReelPos: number;
			// taking previous symbol's reel position or setting it to -1 so that next position will be 0
			if (prevSym) {
				prevElementReelPos = prevSym.positionOnReel;
			} else {
				prevElementReelPos = -1;
			}

			// taking a new symbol from the reel and setting it up
			const currentSym: ReelSymbol = this._symbols[this.topVisibleSymbolIndex - 1];
			currentSym.visible = true;
			currentSym.positionOnReel = prevElementReelPos + symbol.height;
			currentSym.size = {width: 1, height: symbol.height};
			currentSym.setSymbol(this.makeSizedSymbol(symbol.symbol, currentSym.size));
			currentSym.setContainerParameters();

			// coming up with a new y position for this symbol and delay for dropping tween
			const yToPos: number =
				this._offsets.y + this.getYPositionForRow(ReelConstants.MAX_ROW_AMOUNT - (prevElementReelPos + 1 + symbol.height));

			lastDelay = i * AvalancheConstants.TIMER_BETWEEN_SYMBOLS_NEW + this._reelIndex * AvalancheConstants.TIMER_BETWEEN_REELS_NEW;

			// lastDelay = i * 10 + this._reelIndex * 30;

			// making a tween to drop a symbol onto a reel
			lastTween = this.dropNewSymbolToPosition(
				this.topVisibleSymbolIndex - 1,
				yToPos,
				lastDelay,
				AvalancheReel.DROP_REELS_AVALANCHE_EASING,
				150,
			);

			this.topVisibleSymbolIndex--;

			if (isFirst) {
				isFirst = false;

				firstSymbolPos = {x: this._reelIndex, y: ReelConstants.MAX_ROW_AMOUNT - (prevElementReelPos + 1)};
				firstSymbolSize = symbol.height;
			}
		}

		if (lastTween) {
			lastTween.onComplete(() => {
				// here is what's happening after symbols fallen down
				this.cleanAfterFall();
				if (this._onAddingSymbolsFinished) {
					this._onAddingSymbolsFinished();
					this._onAddingSymbolsFinished = null;
				}

				if (symbols.length > 0) {
					// playing dropped sound for the biggest symbol size on the screen
					this.playNewSymbolDroppingSound(this.getBiggestSymbolSizeOf(symbols), lastDelay);
				}
				// playing avalanche landing animation for the first falling symbol
				this.playAvalancheLandingAnimationAt(firstSymbolPos, firstSymbolSize);
			});
		}
	}

	protected getBiggestSymbolSizeOf(symbols: AvalancheNewSymbol[]): number {
		let biggestSize: number = 0;
		for (const sym of symbols) {
			if (sym.height > biggestSize) {
				biggestSize = sym.height;
			}
		}

		return biggestSize;
	}

	protected playAvalancheLandingAnimationAt(pos: SymbolPosition, height: number): void {
		this.spineUtils
			.startSpineAnimationCreation('AVALANCHE_LAND.json', 'LAND', false)
			.position(ReelsUtils.getSymbolScreenPosition(pos))
			.createAndStart();
	}

	protected playNewSymbolDroppingSound(symSize: number, delay: number): void {
		this.soundPlayer.play(`avalancheFallingDownSize${symSize}`);
	}

	/**
	 * getting index position on the reel from position in array
	 * @param positionIndex - array position
	 * @param offset - additional offset (used for height)
	 */
	protected getPositionOnReel(positionIndex: number, offset: number): number {
		return ReelConstants.MAX_ROW_AMOUNT - 1 - positionIndex + offset;
	}

	protected playRemoveAnimationAt(sym: ReelSymbol, onCompleted: Function): void {
		sym.visible = false;
		setTimeout(() => {
			onCompleted && onCompleted();
		}, 0);
	}

	protected hasBounceAnimation(symType: number): boolean {
		return false;
	}

	protected addBounceAnimationTo(symbol: ReelSymbol): void {
	}

	protected dropNewSymbolToPosition(
		symIndex: number,
		yPosition: number,
		delay: number,
		easing: IEasingFunction = Easing.Quadratic.In,
		duration: number = AvalancheConstants.AVALANCHE_NEW_TIMER,
	): Tween {
		this._symbols[symIndex].y =
			yPosition - ReelConstants.getSymbolSlot(this._viewType).height * ReelConstants.MAX_ROW_AMOUNT - ReelConstants.getReelsOffsets(this._viewType).y;
		const dropNewTween: Tween = new Tween(this._symbols[symIndex])
			.to({y: yPosition}, duration)
			.easing(easing)
			.delay(delay);
		this.add(dropNewTween);
		dropNewTween.start();

		this.addShakingLoopToSymbol(symIndex, delay);

		if (this.hasBounceAnimation(this._symbols[symIndex].symbolType)) {
			const timeToBounce: number = AvalancheConstants.AVALANCHE_NEW_TIMER + delay - AvalancheReel.STOP_ANIMATION_TIMER_OFFSET;
			const bounceAnimTimer: Timer = new Timer(
				() => {
					this.addBounceAnimationTo(this._symbols[symIndex]);
				},
				timeToBounce,
				true,
			);
			this.add(bounceAnimTimer);
		}

		return dropNewTween;
	}

	protected addShakingLoopToSymbol(symIndex: number, delay: number): void {
		this._symbols[symIndex].container.rotation = (Math.PI / 180) * -4;
		const symRotTweenRight: Tween = new Tween(this._symbols[symIndex].container)
			.to({rotation: (Math.PI / 180) * 4}, 120)
			.yoyo(true)
			.delay(delay)
			.repeat(AvalancheConstants.AVALANCHE_NEW_TIMER / 180)
			.onComplete(() => {
				this._symbols[symIndex].container.rotation = 0;
			})
			.start();
		this.add(symRotTweenRight);
	}

	protected dropExistingSymbol(symIndex: number, yPosition: number, delay: number, shake: boolean): Tween {
		if (shake) {
			this._symbols[symIndex].container.rotation = (Math.PI / 180) * -4;
		}
		const dropNewTween: Tween = new Tween(this._symbols[symIndex])
			.to({y: yPosition}, AvalancheConstants.AVALANCHE_REPOSITION_TIMER)
			.easing(Easing.SingleBounce.Out)
			.delay(delay);
		this.add(dropNewTween);
		dropNewTween.start();

		if (shake) {
			const symRotTween: Tween = new Tween(this._symbols[symIndex].container)
				.to({rotation: (Math.PI / 180) * 4}, 150)
				.repeat(AvalancheConstants.AVALANCHE_REPOSITION_TIMER / 180)
				.yoyo(true)
				.delay(delay)
				.onComplete(() => {
					this._symbols[symIndex].container.rotation = 0;
				})
				.start();
			this.add(symRotTween);
			symRotTween.start();
		}

		return dropNewTween;
	}

	protected onLastSymbolDroppedOut(): void {
		this._onReelDropOutFinished(this._reelIndex);
	}

	protected onFirstSymbolDropped(): void {
		this._onReelDropFirstSymbolFinished && this._onReelDropFirstSymbolFinished(this._reelIndex);
	}

	protected onLastSymbolDropped(): void {
		this.cleanAfterFall();
		this._onReelDropNewFinished(this._reelIndex);
	}

	protected cleanAfterFall(): void {
		for (let i = 0; i < ReelConstants.MAX_ROW_AMOUNT; i++) {
			this._symbols[i].removeContainerParemeters();
			this._symbols[i].container.rotation = 0;
		}
	}

	protected createReel(initialReel: GameSymbol[]): void {
		for (let rowIndex = 0; rowIndex < ReelConstants.MAX_ROW_AMOUNT; rowIndex++) {
			const symbolsSprite: PIXI.Sprite = new PIXI.Sprite();
			this.addAllSymbolsIntoSprite(symbolsSprite, this._resources);

			const symbolBackground: PIXI.Sprite = new PIXI.Sprite();
			this.addBackgroundsIntoSprite(symbolBackground, this._resources);

			const symbolForeground: PIXI.Sprite = new PIXI.Sprite();
			this.addForegroundIntoSprite(symbolForeground, this._resources);

			const symbol: ReelSymbol = this.createReelSymbol();

			symbol.init(symbolsSprite, symbolBackground, symbolForeground);
			symbol.setSpineUtils(this.spineUtils);
			symbol.setTimerUtils(this._timerUtils);
			this.setInitialSymbolAtPosition(symbol, rowIndex, this._initialStrip[rowIndex]);

			symbol.index = rowIndex;
			symbol.x = this._offsets.x;
			symbol.y = this._offsets.y + this.getYPositionForRow(rowIndex);

			this._symbols[rowIndex] = symbol;
			this._container.addChild(symbol);
		}

		if (initialReel) {
			this.symbolsOnReel = initialReel.length;
		}
	}

	protected addAllSymbolsIntoSprite(spr: PIXI.Sprite, resources: ResourcesLoader): void {
		throw new Error('AvalancheReel::addAllSymbolsIntoSprite must be overriden');
	}

	protected addBackgroundsIntoSprite(spr: PIXI.Sprite, resources: ResourcesLoader): void {
		throw new Error('AvalancheReel::addBackgroundsIntoSprite must be overriden');
	}

	protected addForegroundIntoSprite(spr: PIXI.Sprite, resources: ResourcesLoader): void {
		throw new Error('AvalancheReel::addForegroundIntoSprite must be overriden');
	}

	protected setInitialSymbolAtPosition(symbol: ReelSymbol, rowIndex: number, symbolID: number): void {
		symbol.setSymbol(this.makeSizedSymbol(this._initialStrip[rowIndex], {width: 1, height: 1}));
	}

	protected getYPositionForRow(rowIndex: number): number {
		return rowIndex * ReelConstants.getSymbolSlot(this._viewType).height;
	}

	protected makeSizedSymbol(symbol: number, size: ObjectSize): number {
		throw new Error('AvalancheReel::makeSizedSymbol must be overriden');
	}

	protected createReelSymbol(): ReelSymbol {
		return new ReelSymbol();
	}

	public showAllSymbols(): void {
		for (let i = 0; i < this.symbolsOnReel; i++) {
			this.showSymbolAtPos(i);
		}
	}

	public hideSymbolAtPos(pos: number): void {
		this._symbols[pos].visible = false;
	}

	public showSymbolAtPos(pos: number): void {
		this._symbols[pos].visible = true;
	}

	public get soundPlayer(): SoundPlayer {
		return this._soundPlayer;
	}

	public get spineUtils(): SpineUtils {
		return this._spineUtils;
	}

	public set onReelDropOutFinished(value: Function) {
		this._onReelDropOutFinished = value;
	}

	public set onReelDropFirstSymbolFinished(value: Function) {
		this._onReelDropFirstSymbolFinished = value;
	}

	public set onReelDropNewFinished(value: Function) {
		this._onReelDropNewFinished = value;
	}

	public set onFillingVoidsFinished(value: Function) {
		this._onFillingVoidsFinished = value;
	}

	public set onAddingSymbolsFinished(value: Function) {
		this._onAddingSymbolsFinished = value;
	}

	public set topVisibleSymbolIndex(value: number) {
		this._topVisibleSymbolIndex = value;
	}

	public get topVisibleSymbolIndex(): number {
		return this._topVisibleSymbolIndex;
	}

	public set symbolsOnReel(value: number) {
		this._symbolsOnReel = value;
	}

	public get symbolsOnReel(): number {
		return this._symbolsOnReel;
	}

	public set timerUtils(value: TimerUtils) {
		this._timerUtils = value;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReel.ts